import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import BackButton from '../components/ui/BackButton';

export const MatterPage = () => {
  return (
    <Layout>
      {/* Back Button */}
      <div className="container-xl pt-24 pb-2">
        <BackButton to="/motorization/smart-connectivity" label="Smart Connectivity" />
      </div>

      {/* Hero Section - Full width image with centered text */}
      <section className="relative h-[45vh] lg:h-[55vh] overflow-hidden" data-testid="matter-hero">
        <img 
          src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/q9kggizi_image.png"
          alt="Smart home devices - Google Nest, Amazon Echo, HomePod"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/10" />
        <div className="container-xl relative z-10 h-full flex flex-col justify-start pt-16 items-center text-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-white text-5xl lg:text-6xl font-bold leading-tight mb-4">
              Matter Smart Shades
            </h1>
            <p className="text-2xl lg:text-3xl text-white font-bold">
              The future of smart home has arrived.
            </p>
          </motion.div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="w-10 h-10 rounded-full border-2 border-neutral-400 flex items-center justify-center">
            <svg className="w-5 h-5 text-neutral-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Section 1: Works with all smart home platforms - Image LEFT, Text RIGHT */}
      <section className="py-8 bg-white" data-testid="works-with-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg">
            {/* Image LEFT */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/0wuyqw10_image.png"
                alt="Smart home connectivity with wooden blinds"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Text RIGHT */}
            <div className="bg-[#f5f5f5] flex flex-col justify-center px-10 lg:px-16 py-8">
              <h2 className="text-2xl lg:text-3xl font-serif mb-4">Works with all smart home platforms</h2>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                Matter is here – and Eve Motionblinds motors support the new standard for smart home devices. 
                This means our motors work with all major smart home platforms including Apple Home, Google Home, 
                Amazon Alexa, Samsung SmartThings, and Home Assistant.
              </p>
              <div>
                <Link to="/blog">
                  <Button 
                    variant="outline" 
                    className="border-neutral-300 text-neutral-900 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 rounded-full px-6 py-4 text-sm font-medium bg-transparent"
                  >
                    More about Matter
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Made for everyone - Text LEFT, Image RIGHT */}
      <section className="py-8 bg-white" data-testid="made-for-everyone-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg">
            {/* Text LEFT */}
            <div className="bg-[#f5f5f5] flex flex-col justify-center px-10 lg:px-16 py-8 order-2 lg:order-1">
              <h2 className="text-2xl lg:text-3xl font-serif mb-4">Made for everyone in your home</h2>
              <p className="text-neutral-600 text-sm leading-relaxed">
                Matter-enabled devices are a breeze to integrate into your smart home. Your entire family can 
                control your home through their preferred voice assistant or app. That's because Matter doesn't 
                mind if you use an iPhone, Android device or call upon Alexa, Siri or Google Assistant.
              </p>
            </div>
            
            {/* Image RIGHT */}
            <div className="order-1 lg:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/xywac5ls_image.png"
                alt="HomePod mini and iPhone"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Cutting-edge connectivity - Image LEFT, Text RIGHT */}
      <section className="py-8 bg-white" data-testid="connectivity-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg">
            {/* Image LEFT */}
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/0yx6vhif_image.png"
                alt="Smart home house diagram with connected devices"
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Text RIGHT */}
            <div className="bg-[#f5f5f5] flex flex-col justify-center px-10 lg:px-16 py-8">
              <h2 className="text-2xl lg:text-3xl font-serif mb-4">Cutting-edge connectivity</h2>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                Matter devices truly shine when they talk via Thread. This groundbreaking network technology 
                makes your smart home more responsive, robust, and increases its reach. That is why all Eve 
                Motionblinds motors embrace Thread. To control Eve Motionblinds with Matter, you need a border 
                router with Thread and Matter from the smart home platform you choose.
              </p>
              <div>
                <Link to="/motorization/smart-connectivity/hubs">
                  <Button 
                    variant="outline" 
                    className="border-neutral-300 text-neutral-900 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 rounded-full px-6 py-4 text-sm font-medium bg-transparent"
                  >
                    Choose the right hub
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Upgrade your blinds - Text LEFT, Image RIGHT */}
      <section className="py-8 bg-white" data-testid="upgrade-section">
        <div className="container-xl">
          <div className="grid lg:grid-cols-2 overflow-hidden rounded-lg">
            {/* Text LEFT */}
            <div className="bg-[#f5f5f5] flex flex-col justify-center px-10 lg:px-16 py-8 order-2 lg:order-1">
              <h2 className="text-2xl lg:text-3xl font-serif mb-4">Upgrade your existing Eve Motionblinds</h2>
              <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                If you have purchased roller blinds powered by Eve Motionblinds in the past, and want to leverage 
                Matter to use your blinds from an additional platform, you're eligible for a free firmware upgrade.
              </p>
              <div>
                <Link to="/contact">
                  <Button 
                    variant="outline" 
                    className="border-neutral-300 text-neutral-900 hover:bg-neutral-900 hover:text-white hover:border-neutral-900 rounded-full px-6 py-4 text-sm font-medium bg-transparent"
                  >
                    Upgrade your Eve Motionblinds
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Image RIGHT */}
            <div className="order-1 lg:order-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_roller-shade-builder/artifacts/3aew4kk5_image.png"
                alt="Person using phone to scan QR code on blinds"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Good to know - FAQ Section */}
      <section className="py-16 bg-white" data-testid="faq-section">
        <div className="container-xl max-w-4xl">
          <h2 className="text-3xl lg:text-4xl font-serif mb-12">Good to know</h2>
          
          {/* FAQ Item 1 */}
          <div className="mb-10">
            <h3 className="text-lg font-semibold mb-4">What has Thread got to do with Matter?</h3>
            <p className="text-neutral-600 text-sm leading-relaxed">
              Apart from unpractical bridges, there are two direct connection technologies that your smart home 
              accessories can use to support Matter: Wi-Fi and Thread. Thread offers several advantages over Wi-Fi, 
              including robust mesh technology and incredible energy efficiency.{' '}
              <a href="https://www.evehome.com/en/thread" target="_blank" rel="noopener noreferrer" className="text-amber-700 hover:underline">
                Learn more about the advantages of Thread here
              </a>.
            </p>
          </div>
          
          {/* FAQ Item 2 */}
          <div className="mb-10">
            <h3 className="text-lg font-semibold mb-4">What's the difference between HomeKit and Matter?</h3>
            <p className="text-neutral-600 text-sm leading-relaxed">
              Matter is a new smart home protocol developed jointly by Amazon, Apple, Google, Samsung and many other 
              manufacturers to eliminate incompatibilities in the smart home once and for all. The protocol incorporates 
              many benefits of HomeKit, including an ultra-secure and completely local infrastructure – all without the 
              need for an internet connection. If HomeKit impressed you, you'll be thrilled about Matter.
            </p>
          </div>
          
          {/* FAQ Item 3 */}
          <div className="mb-10">
            <h3 className="text-lg font-semibold mb-4">What's going to happen with my HomeKit Eve Motionblinds?</h3>
            <p className="text-neutral-600 text-sm leading-relaxed">
              If you're already using Thread-enabled Eve Motionblinds,{' '}
              <Link to="/contact" className="text-amber-700 hover:underline">free firmware updates</Link>{' '}
              will be made available over time to seamlessly upgrade them to Matter. You'll then be able to use those 
              Eve Motionblinds not only with HomeKit, but also with other platforms, including Amazon Alexa, Google Home, 
              or Samsung SmartThings.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#f5f5f5]" data-testid="cta-section">
        <div className="container-xl">
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Where to buy Motionblinds?</h3>
              <p className="text-neutral-500 mb-6 text-sm">Find a retailer or dealer near you.</p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-full px-6 text-sm">
                    Find your store
                  </Button>
                </Link>
                <Link to="/trade">
                  <Button variant="outline" className="border-neutral-300 text-neutral-900 hover:bg-neutral-900 hover:text-white rounded-full px-6 text-sm">
                    For professionals
                  </Button>
                </Link>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Support center | FAQ</h3>
              <p className="text-neutral-500 mb-6 text-sm">Get answers to common questions.</p>
              <Link to="/faq">
                <Button className="bg-neutral-900 hover:bg-neutral-800 text-white rounded-full px-6 text-sm">
                  Find your answer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MatterPage;
